import { useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { DatePicker as MuiDatePicker, DesktopDatePicker } from '@mui/x-date-pickers'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { isValid } from 'date-fns'

import { DefaultElementProps } from 'common/interfaces'
import { textFieldPickerStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import { useTemplate } from 'utils/hook'
import useSetValueAsTZFormat from 'utils/useSetValueAsTZFormat'
import validationsToRules from 'utils/validationsToRules'

import { DatePicker as DatePickerProps } from './interface'

const pickerYearButtonSelector = '.MuiPickersYear-yearButton'
const muiSelectedClass = 'Mui-selected'

function getDatePickerOwnerId(name: string): string {
  return `date-picker-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}-picker`
}

function getOpenPickerRoot(name: string): Element | null {
  return document.getElementById(getDatePickerOwnerId(name))
}

function toDateOnlyIso(date: Date): string {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  return dateOnly.toISOString()
}

function parseDateValue(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Date) return isValid(val) ? val : null
  if (typeof val === 'string') {
    const date = new Date(val)
    return isValid(date) ? date : null
  }

  return null
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function isFutureDate(date: Date): boolean {
  return startOfDay(date) > startOfDay(new Date())
}

const pickerErrorMessages: Record<string, string> = {
  invalidDate: 'Your date in wrong format',
  disableFuture: 'Date cannot be in the future',
  disablePast: 'Date cannot be in the past',
  minDate: 'Date is before the minimum allowed',
  maxDate: 'Date is after the maximum allowed',
}

export function annotatePickerYearButtons(container: ParentNode): void {
  container.querySelectorAll<HTMLButtonElement>(pickerYearButtonSelector).forEach((button) => {
    const year = button.textContent?.trim()
    if (year === undefined || year === '') {
      return
    }

    const isSelected = button.classList.contains(muiSelectedClass)
    button.setAttribute('aria-label', isSelected ? `Year ${year}, selected` : `Year ${year}`)

    if (isSelected) {
      button.setAttribute('aria-pressed', 'true')
    } else {
      button.removeAttribute('aria-pressed')
    }
  })
}

function AccessiblePickersDay(props: PickersDayProps<Date>): JSX.Element {
  const { day, selected, today, ...other } = props
  const dateLabel = day.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const stateLabel = [today === true ? 'today' : undefined, selected === true ? 'selected' : undefined]
    .filter(Boolean)
    .join(', ')
  const label = stateLabel === '' ? dateLabel : `${dateLabel}, ${stateLabel}`

  return (
    <PickersDay
      {...other}
      day={day}
      selected={selected}
      today={today}
      aria-label={label}
      aria-current={today === true ? 'date' : undefined}
      aria-pressed={selected === true ? 'true' : undefined}
    />
  )
}

export default function DatePicker({
  name,
  size,
  label,
  onFocus,
  onChange,
  validations,
  defaultValue,
  disableFuture,
  allowTyping = false,
}: DatePickerProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()

  const { control, setValue, setError, clearErrors } = useFormContext()
  const baseRules = validationsToRules(validations)
  // MUI onError + setError alone is not enough: trigger() revalidates the registered
  // value and clears setError. Keep the active picker error so Continue can block.
  const pickerErrorRef = useRef<string | null>(null)
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      ...baseRules,
      validate: {
        ...baseRules?.validate,
        // Ensure Continue/trigger() blocks when the visible typed input is invalid,
        // even if a previous valid ISO is still (or was) registered.
        typedDateValid: () => {
          if (!allowTyping || !pickerErrorRef.current) {
            return true
          }

          return pickerErrorMessages[pickerErrorRef.current] ?? pickerErrorMessages.invalidDate
        },
        // Ensure Continue/trigger() blocks future dates when disableFuture is set
        // (setError alone is cleared by trigger if the field only has `required`).
        notInFuture: (value) => {
          if (!disableFuture || !value) {
            return true
          }

          const date = parseDateValue(value)
          if (!date) {
            return true
          }

          return !isFutureDate(date) || pickerErrorMessages.disableFuture
        },
      },
    },
    defaultValue: render(defaultValue ? new Date(defaultValue).toISOString() : ''),
  })

  // Local value so MUI section typing can show partial dates without RHF resetting the field.
  const [pickerValue, setPickerValueState] = useState<Date | null>(() => parseDateValue(field.value))
  const pickerValueRef = useRef<Date | null>(pickerValue)

  const setPickerValue = (value: Date | null): void => {
    pickerValueRef.current = value
    setPickerValueState(value)
  }

  useEffect(() => {
    // While the typed input is invalid, keep local picker state (do not reset from form).
    if (pickerErrorRef.current) {
      return
    }

    setPickerValue(parseDateValue(field.value))
  }, [field.value])

  const commitValidDate = (date: Date): void => {
    const valueInISOString = toDateOnlyIso(date)
    setPickerValue(date)
    setValue(name, valueInISOString, { shouldValidate: true })
    onChange?.({ value: valueInISOString })
  }

  const onChangeDatePicker = (newValue: Date | null): void => {
    setPickerValue(newValue)

    if (newValue && isValid(newValue)) {
      if (disableFuture && isFutureDate(newValue)) {
        pickerErrorRef.current = 'disableFuture'
        setValue(name, toDateOnlyIso(newValue), { shouldValidate: true })
        setError(name, { type: 'validate', message: pickerErrorMessages.disableFuture })
        return
      }

      pickerErrorRef.current = null
      clearErrors(name)
      commitValidDate(newValue)
      return
    }

    // Incomplete / cleared while typing — keep local UI state, clear committed form value.
    if (newValue === null) {
      pickerErrorRef.current = null
      clearErrors(name)
      setValue(name, '')
    }
  }

  const onPickerError = (reason: string | null): void => {
    if (!allowTyping) {
      return
    }

    if (reason) {
      pickerErrorRef.current = reason
      // Drop any previously committed ISO so submit paths cannot use a stale valid date.
      setValue(name, '')
      setError(name, {
        type: 'validate',
        message: pickerErrorMessages[reason] ?? pickerErrorMessages.invalidDate,
      })
    } else {
      const currentPickerValue = pickerValueRef.current
      if (
        disableFuture &&
        currentPickerValue !== null &&
        isValid(currentPickerValue) &&
        isFutureDate(currentPickerValue)
      ) {
        pickerErrorRef.current = 'disableFuture'
        setError(name, { type: 'validate', message: pickerErrorMessages.disableFuture })
        return
      }

      pickerErrorRef.current = null
      clearErrors(name)
    }
  }

  useSetValueAsTZFormat(field)

  const [open, setOpen] = useState<boolean>(false)

  const showPicker = (): void => {
    setOpen(true)
  }

  const hidePicker = (): void => {
    setOpen(false)
  }

  const pickerOwnerId = getDatePickerOwnerId(name)

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const annotateCurrentPickerYears = (): void => {
      const pickerRoot = getOpenPickerRoot(name)

      if (pickerRoot !== null) {
        annotatePickerYearButtons(pickerRoot)
      }
    }

    annotateCurrentPickerYears()

    const observer = new MutationObserver(annotateCurrentPickerYears)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [open, pickerValue, name])

  const { ref: _fieldRef, value: _fieldValue, onChange: _fieldOnChange, ...fieldRest } = field

  // allowTyping: DesktopDatePicker keeps an editable field + calendar icon on mobile
  // (adaptive MobileDatePicker forces readOnly and opens on field tap with no icon).
  // Calendar-only: keep adaptive DatePicker (mobile dialog on tap).
  const PickerComponent = allowTyping ? DesktopDatePicker : MuiDatePicker

  return (
    <SizingContainer className="date-picker-wrap" size={size} minWidth={150}>
      <PickerComponent
        {...fieldRest}
        value={pickerValue}
        disableFuture={disableFuture}
        label={label}
        onChange={onChangeDatePicker}
        onError={onPickerError}
        onOpen={showPicker}
        onClose={hidePicker}
        open={open}
        slots={{
          day: AccessiblePickersDay,
        }}
        slotProps={{
          popper: {
            id: pickerOwnerId,
          },
          mobilePaper: {
            id: pickerOwnerId,
          },
          openPickerButton: allowTyping
            ? {
                'aria-label': `Open calendar for ${label}`,
                // Keep the icon easy to tap on phones.
                sx: { touchAction: 'manipulation' },
              }
            : undefined,
          textField: {
            id: name,
            required: validations?.required?.value,
            sx: textFieldPickerStyle,
            fullWidth: true,
            // Calendar-only (default): click opens picker, input not editable.
            // allowTyping: type in the field; open calendar via the icon.
            onClick: allowTyping ? undefined : showPicker,
            onFocus,
            error: error !== undefined,
            helperText: error?.message,
            inputProps: {
              readOnly: !allowTyping,
              inputMode: allowTyping ? 'numeric' : undefined,
              'aria-invalid': error !== undefined,
              'aria-describedby': error !== undefined ? `${name}-helper-text` : undefined,
            },
            FormHelperTextProps: {
              id: `${name}-helper-text`,
              role: error !== undefined ? 'alert' : undefined,
            },
          },
        }}
      />
    </SizingContainer>
  )
}
